import { AllDimensions, Dimension } from '@pubstack/common/src/analytics/dimension'
import { AllQuery, AnalyticsQueryArgs, AnalyticsQueryFilter } from '@pubstack/common/src/analytics/query'
import { formatToDateWithoutTime, parseFromDateWithoutTime, sameDay } from '@pubstack/common/src/date'
import { useQuery as useTanstackQuery } from '@tanstack/react-query'
import { DateTime } from 'luxon'
import { post } from '~/api/api-client'
import { Context, Filter } from '~/state'
import { isFilterAvailable } from '~/utils/analytics'

const getAnalyticsQueryFiltersFromContext = (context: Context, dimensions: Dimension[] = [...AllDimensions]): AnalyticsQueryFilter[] =>
  (Object.entries(context.filters) as [Dimension, Filter][])
    .filter(([dimension]) => dimensions.includes(dimension))
    .filter(([_, filter]) => isFilterAvailable(filter, dimensions))
    .map(([dimension, filter]) => {
      return { dimension: dimension as Dimension, excluded: filter.mode === 'exclude', values: filter.values.map((v) => v.value) }
    })

export const useAnalyticsQueryTanstack = <T>(query: AllQuery, dimensions: Dimension[], context: Context, args: AnalyticsQueryArgs = {}, placeHolderData = true, enabled = true) => {
  const analyticsQueryFilters: AnalyticsQueryFilter[] = getAnalyticsQueryFiltersFromContext(context, dimensions)
  const queryId = crypto.randomUUID()
  const body = {
    query,
    args,
    context: { filters: analyticsQueryFilters, from: formatToDateWithoutTime(context.timeRange.from), to: formatToDateWithoutTime(context.timeRange.to), tz: context.timeRange.tz },
    queryId,
  }
  return useTanstackQuery({
    queryKey: ['analytics', body.query, body.args, body.context],
    queryFn: ({ signal }) => {
      signal?.addEventListener('abort', () => {
        post<T>(
          '/analytics/query-cancel',
          {
            queryId,
          },
          new AbortController().signal
        )
      })
      return post<T>('/analytics/query', body, signal)
    },
    staleTime: sameDay(parseFromDateWithoutTime(body.context.to), DateTime.now()) ? 60 * 1000 : Infinity,
    placeholderData: placeHolderData ? (previousData) => previousData : undefined,
    enabled,
    retry: false,
  })
}

export const useAnalyticsSearchTanstack = <T>(dimensions: Dimension[], context: Context, args: AnalyticsQueryArgs & { term: string }, placeHolderData = true, enabled = true) => {
  const analyticsQueryFilters: AnalyticsQueryFilter[] = getAnalyticsQueryFiltersFromContext(context, dimensions)
  const queryId = crypto.randomUUID()
  const body = {
    args,
    context: {
      filters: analyticsQueryFilters.filter((filter) => filter.dimension !== args.dimension),
      from: formatToDateWithoutTime(context.timeRange.from),
      to: formatToDateWithoutTime(context.timeRange.to),
      tz: context.timeRange.tz,
    },
    queryId,
  }
  return useTanstackQuery({
    queryKey: ['analytics', 'search', body.args, body.context],
    queryFn: ({ signal }) => {
      signal?.addEventListener('abort', () => {
        post<T>(
          '/analytics/query-cancel',
          {
            body: {
              queryId,
            },
          },
          new AbortController().signal
        )
      })
      return post<T>('/analytics/search', body, signal)
    },
    staleTime: sameDay(parseFromDateWithoutTime(body.context.to), DateTime.now()) ? 60 * 1000 : Infinity,
    placeholderData: placeHolderData ? (previousData) => previousData : undefined,
    enabled,
    retry: false,
  })
}
